/** The Trips panel's queries (plan "Page anatomy" → Trips panel, D5): one infinite query on
 *  `GET /bookings/` per group, each pinned to the user and to its phase
 *  (`tripsListParams`), pages of 25 with the exact count of the first. Past loads its first
 *  page with the rest — the header's count and the axis's recent past need it — and its
 *  further pages on demand, like Upcoming's.
 *
 *  A group's failure is its own: the panel shows Retry in place of that group, never blanks
 *  the others. */
import { useInfiniteQuery, type InfiniteData, type UseInfiniteQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import * as bookingsApi from '@/api/bookings';
import type { BookingListResponse } from '@/api/bookings';

import { tripsListParams, type TripGroupData, type TripGroupId, type TripGroups } from './tripsModel';

export const userTripsKey = (userId: number, group: TripGroupId) => ['bookings', 'userTrips', userId, group] as const;

type Pages = InfiniteData<BookingListResponse, number>;
type GroupResult = UseInfiniteQueryResult<Pages, Error>;

/** A group's request state, beside its data in `groups`. */
export interface TripGroupStatus {
  isLoading: boolean;
  error: Error | null;
  isFetchingMore: boolean;
  loadMore: () => void;
  retry: () => void;
}

function useGroup(userId: number, group: TripGroupId, enabled: boolean): GroupResult {
  return useInfiniteQuery({
    queryKey: userTripsKey(userId, group),
    queryFn: ({ pageParam }) => bookingsApi.list(tripsListParams(userId, group, pageParam)),
    initialPageParam: 1,
    getNextPageParam: (last, all) => (last.next ? all.length + 1 : undefined),
    enabled,
  });
}

function toData(data: Pages | undefined): TripGroupData {
  const pages = data?.pages ?? [];
  const first = pages[0];
  return {
    rows: pages.flatMap((p) => p.results),
    count: first ? Math.max(0, first.count) : null,
    capped: Boolean(first?.count_is_capped),
    hasMore: Boolean(pages[pages.length - 1]?.next),
  };
}

function toStatus(q: GroupResult): TripGroupStatus {
  return {
    isLoading: q.isLoading,
    error: q.error,
    isFetchingMore: q.isFetchingNextPage,
    loadMore: () => void q.fetchNextPage(),
    retry: () => void q.refetch(),
  };
}

/** The four groups of a person's trips (`groups`, for the pure model) and each group's
 *  request state; nothing is asked while `enabled` is false. */
export function useUserTrips(
  userId: number,
  enabled: boolean,
): { groups: TripGroups; status: Record<TripGroupId, TripGroupStatus> } {
  const active = useGroup(userId, 'active', enabled);
  const future = useGroup(userId, 'future', enabled);
  const past = useGroup(userId, 'past', enabled);
  const undated = useGroup(userId, 'undated', enabled);
  const groups = useMemo<TripGroups>(
    () => ({ active: toData(active.data), future: toData(future.data), past: toData(past.data), undated: toData(undated.data) }),
    [active.data, future.data, past.data, undated.data],
  );
  return {
    groups,
    status: { active: toStatus(active), future: toStatus(future), past: toStatus(past), undated: toStatus(undated) },
  };
}
